import React, { useEffect, useState, useRef } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Upload as UploadIcon, FileText, Check, Lock, LockOpen, KeyRound } from 'lucide-react';
import bcrypt from 'bcryptjs';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { encryptFile } from '../lib/encryption';
import { sanitiseFilename } from '../lib/validation';
import { getRemainingBytes, isQuotaExceeded } from '../lib/quota';

const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;
const SELECTED_FOLDER_STORAGE_KEY = 'securevault_selected_folder_id';

const UploadPage = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [doEncrypt, setDoEncrypt] = useState(true);
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState('');
  const [encSteps, setEncSteps] = useState([]);
  const [downloadPw, setDownloadPw] = useState('');
  const [folders, setFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(sessionStorage.getItem(SELECTED_FOLDER_STORAGE_KEY) || '');
  const [isDragging, setIsDragging] = useState(false);
  const navigate = useNavigate();
  const { user, logAction } = useAuth();
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchFolders = async () => {
      if (!user) return;

      const { data, error } = await supabase
        .from('folders')
        .select('id,name')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        toast.error('Failed to fetch folders');
        return;
      }

      setFolders(data || []);
    };

    fetchFolders();
  }, [user]);

  const processSelectedFile = (selectedFile) => {
    if (selectedFile) {
      if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
        toast.error('File size exceeds the 50MB limit');
        setFile(null);
        return;
      }

      const safeFileName = sanitiseFilename(selectedFile.name);
      const safeFile = safeFileName === selectedFile.name
        ? selectedFile
        : new File([selectedFile], safeFileName, { type: selectedFile.type, lastModified: selectedFile.lastModified });

      setFile(safeFile);
      setUploadSuccess(false);

      if (selectedFile.name !== safeFileName) {
        toast.warning('Filename contains unsupported characters. They will be replaced with underscores.');
      }
    }
  };

  const handleFileChange = (e) => {
    processSelectedFile(e.target.files[0]);
    e.target.value = '';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    processSelectedFile(e.dataTransfer.files[0]);
  };

  const handleUpload = async () => {
    if (!file || !user) {
      toast.error('Please select a file');
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.error('File size exceeds the 50MB limit');
      return;
    }

    const { data: existingFiles, error: quotaError } = await supabase
      .from('files')
      .select('file_size')
      .eq('user_id', user.id);

    if (quotaError) {
      toast.error('Unable to check storage quota. Please try again.');
      return;
    }

    const totalUsed = (existingFiles || []).reduce((sum, item) => sum + (item.file_size || 0), 0);

    if (isQuotaExceeded(totalUsed)) {
      toast.error('Storage limit reached. Delete some files to free up space.');
      return;
    }

    if (file.size > getRemainingBytes(totalUsed)) {
      toast.error('This file would exceed your 1GB storage limit.');
      return;
    }

    setUploading(true);
    setProgress(0);
    setEncSteps([]);

    const addStep = (text) => setEncSteps(prev => [...prev, text]);

    try {
      addStep('Reading file into memory...');
      setStage('Reading file...');
      const fileBuffer = await file.arrayBuffer();
      setProgress(5);

      // Compute SHA-256 hash
      addStep('Computing SHA-256 file hash...');
      setStage('Hashing...');
      const hashBuffer = await crypto.subtle.digest('SHA-256', fileBuffer);
      const fileHash = Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');
      addStep(`Hash: ${fileHash.slice(0, 16)}...${fileHash.slice(-8)}`);
      setProgress(10);

      let uploadData;
      let encryptionData = null;

      if (doEncrypt) {
        addStep('Generating AES-256 encryption key...');
        setStage('Encrypting...');
        setProgress(12);
        await new Promise(r => setTimeout(r, 300));
        addStep('Creating 96-bit initialization vector (IV)...');
        setProgress(14);
        await new Promise(r => setTimeout(r, 200));
        addStep('Encrypting with AES-256-GCM mode...');
        const encrypted = await encryptFile(fileBuffer);
        uploadData = encrypted.encryptedData;
        encryptionData = encrypted.encryptionData;
        addStep('Encryption complete. Authentication tag generated.');
        setProgress(20);
      } else {
        uploadData = new Uint8Array(fileBuffer);
        setProgress(15);
      }

      const safeFileName = sanitiseFilename(file.name);
      const ext = safeFileName.split('.').pop() || 'bin';
      const storagePath = `${user.id}/${crypto.randomUUID()}.${ext}`;

      addStep('Uploading to Supabase Storage...');
      setStage('Uploading...');
      const session = await supabase.auth.getSession();
      const token = session.data?.session?.access_token;

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/vault-files/${storagePath}`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.setRequestHeader('apikey', process.env.REACT_APP_SUPABASE_ANON_KEY);
        xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
        xhr.setRequestHeader('x-upsert', 'false');
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setProgress(Math.round(20 + (e.loaded / e.total) * 70));
        };
        xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(xhr.responseText || 'Upload failed'));
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(new Blob([uploadData], { type: file.type || 'application/octet-stream' }));
      });

      addStep('Saving file metadata to database...');
      setStage('Saving metadata...');
      setProgress(92);
      const trimmedDownloadPassword = downloadPw.trim();
      const hashedDownloadPassword = trimmedDownloadPassword ? bcrypt.hashSync(trimmedDownloadPassword, 10) : null;

      const { error: dbError } = await supabase.from('files').insert({
        user_id: user.id,
        file_name: safeFileName,
        file_size: file.size,
        storage_path: storagePath,
        encryption_iv: encryptionData,
        file_hash: fileHash,
        download_password: hashedDownloadPassword,
        folder_id: selectedFolderId || null,
      });

      if (dbError) throw dbError;

      setProgress(100);
      setStage('Complete!');

      await logAction(`Uploaded file: ${safeFileName}${doEncrypt ? ' (encrypted)' : ''}`);
      toast.success('File uploaded successfully!');
      setUploadSuccess(true);
      setFile(null);
      setTimeout(() => navigate('/files'), 1500);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Upload failed');
      setProgress(0);
      setStage('');
    } finally {
      setUploading(false);
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="upload-title">
            Upload File
          </h1>
          <p className="text-base text-muted-foreground mt-1">Files can be encrypted with AES-256-GCM before storage</p>
        </div>

        <div className="max-w-2xl">
          <div
            className={`bg-card border-2 border-dashed rounded-lg p-6 sm:p-12 transition-colors duration-150 ${
              isDragging ? 'border-primary bg-primary/5' : 'border-border'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            data-testid="upload-drop-zone"
          >
            <div className="text-center">
              {!uploadSuccess ? (
                <>
                  {/* Progress Bar - shown during upload */}
                  {uploading && (
                    <div className="mb-6" data-testid="upload-progress-container">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="flex-1 h-2.5 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary rounded-full transition-all duration-300 ease-out"
                            style={{ width: `${progress}%` }}
                            data-testid="upload-progress-bar"
                          />
                        </div>
                        <span className="text-sm font-mono font-semibold text-foreground min-w-[48px] text-right" data-testid="upload-progress-percent">
                          {progress}%
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground font-mono">{stage}</p>
                    </div>
                  )}

                  <UploadIcon className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">{isDragging ? 'Drop file here' : 'Upload your file'}</h3>
                  <p className="text-sm text-muted-foreground mb-6">
                    {doEncrypt ? 'Your file will be encrypted client-side before upload' : 'Upload without encryption'}
                  </p>

                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    className="hidden"
                    data-testid="file-input"
                  />
                  <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="w-full sm:w-auto" data-testid="select-file-btn">
                    <FileText className="w-4 h-4 mr-2" />
                    Select File
                  </Button>

                  {file && (
                    <div className="mt-6 p-4 bg-muted rounded-md" data-testid="file-preview">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <FileText className="w-5 h-5 text-primary" />
                        <div className="text-center sm:text-left min-w-0">
                          <p className="text-sm font-medium truncate" title={file.name}>{file.name}</p>
                          <p className="text-xs text-muted-foreground font-mono">{formatBytes(file.size)}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Encryption Toggle */}
                  <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
                    <Switch id="encrypt-toggle" checked={doEncrypt} onCheckedChange={setDoEncrypt} data-testid="encrypt-toggle" />
                    <Label htmlFor="encrypt-toggle" className="flex items-center justify-center gap-2 cursor-pointer text-center">
                      {doEncrypt ? <Lock className="w-4 h-4 text-[hsl(var(--success))]" /> : <LockOpen className="w-4 h-4 text-muted-foreground" />}
                      {doEncrypt ? 'AES-256-GCM Encryption ON' : 'No Encryption'}
                    </Label>
                  </div>

                  {!doEncrypt && (
                    <div className="mt-4 rounded-md border border-yellow-500/40 bg-yellow-500/10 p-3 text-left" data-testid="encryption-off-warning">
                      <p className="text-xs text-yellow-700 dark:text-yellow-300 leading-relaxed">
                        Warning: Disabling encryption means your file will be stored without AES-256-GCM protection. Only disable this if you intentionally want an unencrypted file.
                      </p>
                    </div>
                  )}

                  {/* Folder Selection */}
                  {folders.length > 0 && (
                    <div className="mt-6 w-full max-w-xs mx-auto text-left">
                      <Label htmlFor="folder-select" className="text-xs mb-1 block">
                        Save to folder (optional)
                      </Label>
                      <select
                        id="folder-select"
                        value={selectedFolderId}
                        onChange={(e) => {
                          setSelectedFolderId(e.target.value);
                          if (e.target.value) {
                            sessionStorage.setItem(SELECTED_FOLDER_STORAGE_KEY, e.target.value);
                          } else {
                            sessionStorage.removeItem(SELECTED_FOLDER_STORAGE_KEY);
                          }
                        }}
                        className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                        data-testid="upload-folder-select"
                      >
                        <option value="">All Files</option>
                        {folders.map((folder) => (
                          <option key={folder.id} value={folder.id}>
                            {folder.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Download Password (optional) */}
                  {file && (
                    <div className="mt-4 w-full max-w-xs mx-auto">
                      <Label htmlFor="dl-pw" className="text-xs flex items-center gap-1 justify-center mb-1">
                        <KeyRound className="w-3 h-3" /> Download Password (optional)
                      </Label>
                      <Input id="dl-pw" type="password" placeholder="Leave empty for no password" value={downloadPw} onChange={e => setDownloadPw(e.target.value.slice(0, 100))} maxLength={100} data-testid="download-password-input" className="w-full text-center text-sm" />
                    </div>
                  )}

                  {/* Encryption Visualizer */}
                  {uploading && encSteps.length > 0 && (
                    <div className="mt-6 text-left bg-muted rounded-md p-4 max-h-40 overflow-y-auto" data-testid="encryption-visualizer">
                      {encSteps.map((step, i) => (
                        <div key={i} className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2 py-1">
                          <span className="text-[hsl(var(--success))] text-xs mt-0.5">&#9654;</span>
                          <span className="text-xs font-mono text-muted-foreground">{step}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {file && (
                    <Button onClick={handleUpload} disabled={uploading} className="mt-6 w-full sm:w-auto" data-testid="upload-submit-btn">
                      {uploading ? 'Uploading...' : doEncrypt ? 'Encrypt & Upload' : 'Upload'}
                    </Button>
                  )}
                </>
              ) : (
                <div className="py-8" data-testid="upload-success">
                  <div className="w-16 h-16 rounded-full bg-[hsl(var(--success))]/10 flex items-center justify-center mx-auto mb-4">
                    <Check className="w-8 h-8 text-[hsl(var(--success))]" />
                  </div>
                  <h3 className="text-lg font-medium mb-2">Upload Successful!</h3>
                  <p className="text-sm text-muted-foreground">Redirecting to your files...</p>
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 bg-card border border-border rounded-lg p-6">
            <h3 className="text-sm font-semibold mb-3 uppercase tracking-[0.2em]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Security Details
            </h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="text-[hsl(var(--success))] mt-0.5">&#10003;</span>
                <span>AES-256-GCM encryption (when enabled)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[hsl(var(--success))] mt-0.5">&#10003;</span>
                <span>Unique encryption key per file</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[hsl(var(--success))] mt-0.5">&#10003;</span>
                <span>Automatic decryption on download</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[hsl(var(--success))] mt-0.5">&#10003;</span>
                <span>RLS-enforced file ownership</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default UploadPage;
